import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';

import { UserPurposeValues, UserIntentValues } from '../definitions/InsCommon';
import Construction from './Construction';
import Property from './Property';
import Occupant from './Occupant';
import Coverage from './Coverage';


const Application = types
    .model('Application', {
        construction: types.optional(Construction, {}),
        occupant: types.optional(Occupant, {}),
        property: types.optional(Property, {}),
        coverage: types.optional(Coverage, {}),

        creditCheckConsent: types.maybeNull(types.boolean),
        hasCAAMembership: types.maybeNull(types.boolean),
        isSmoker: types.maybeNull(types.boolean),
        newsletterSignupConsent: types.maybeNull(types.boolean),
        wantsToBundleInsurance: types.maybeNull(types.boolean),

        userPurpose: types.maybeNull(types.enumeration(Object.values(UserPurposeValues))),
    })
    .views(self => ({
        get intent() {
            return self.userPurpose === UserPurposeValues.NEW_HOME || self.userPurpose === UserPurposeValues.RENEWAL
                ? UserIntentValues.HIGH
                : UserIntentValues.LOW;
        },
        get isComplete() {
            return (
                self.construction.isComplete
                && self.occupant.isComplete
                && self.property.isComplete
                && self.coverage.isComplete
                && self.creditCheckConsent !== null
                && self.hasCAAMembership !== null
                && self.isSmoker !== null
                && self.newsletterSignupConsent !== null
                && self.wantsToBundleInsurance !== null
            );
        },
    }))
    .actions(self => ({
        setProperty(property) {
            self.property = property;
        },
        setOccupant(occupant) {
            self.occupant = occupant;
        },
        setConstruction(construction) {
            self.construction = construction;
        },
        /**
         * Updates the Application model (documents/ins-condo-document/v1/model/Application) with applicationUpdates
         *
         * @param {Object} applicationUpdates base Application model fields (see model for fields)
         * @param {Object} applicationUpdates.Occupant base Occupant model fields (see model for fields)
         * @param {Object} applicationUpdates.Property base Property model fields (see model for fields)
         * @param {Object} applicationUpdates.Construction base Construction model fields (see model for fields)
         */
        update(applicationUpdates) {
            const { property, construction, occupant, ...application } = applicationUpdates;
            const applicationSnapshot = getSnapshot(self);

            // We need to call applySnapshot on the main node to resolve the root level items
            applySnapshot(self, { ...applicationSnapshot, ...application });

            if (property) {
                self.setProperty(resolveNode(applicationSnapshot, 'property', property));
            }
            if (construction) {
                self.setConstruction(resolveNode(applicationSnapshot, 'construction', construction));
            }
            if (occupant) {
                self.setOccupant(resolveNode(applicationSnapshot, 'occupant', occupant));
            }
        },
        updateUserLanguage(language) {
            self.occupant = {
                ...self.occupant,
                language,
            };
        },
    }));

/**
 * Spread the retrieved getSnapshot node (object key) with the values object
 *
 * @param {string} node The getSnapshot node
 * @param {Object} updateValue Updated values for the node
 *
 * @throws {Error} If snapshot node isn't found throws error
 *
 * @returns {Object}
 */
function resolveNode(snapshot, node, updateValue) {
    const { [node]: model } = snapshot;

    if (model === undefined) {
        throw new Error(`Node ${node} not present in model snapshot.`);
    }

    return { ...model, ...updateValue };
}

export default Application;
